import { useContext, useEffect, useRef, useState } from 'react';
import useStakeholderNotesFunctions from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderNotesFunctions';
import time from 'utils/time';
import { useAuth0 } from '@auth0/auth0-react';
import { Portal } from 'react-portal';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { store } from 'components/Store';
import ModalComponent from 'components/Common/Modal';
import DescriptionTinyEditor, { blockquoteStyles } from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import {
  AgencyImage,
  AgencyLogoAndExplanation,
  OrganizationImage,
} from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/NoteVisibility';

const Notes = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, activePlan } = state;

  const { StakeholderInformation, contentType, selectedVisibility, setSelectedVisibility, notesFunctions } = props;
  const { user } = useAuth0();
  const { objectId } = StakeholderInformation;
  // BioElements pre-loads the notes list to show the tab count; reuse that instance when passed.
  const localNotesFunctions = useStakeholderNotesFunctions();
  const { isLoading, userNotes, getNotes, addNote, updateNote, deleteNote, isNoteCreator } =
    notesFunctions ?? localNotesFunctions;
  const [disabledTextArea, setDisabledTextArea] = useState(false);
  const [noteToDeleteId, setNoteToDeleteId] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [heightContainer, containerRef] = useHeightContainer();

  const { newAgencyPlan } = useTopicOrClientWord();

  const isMobile = useIsMobile();
  const editorRef = useRef(null);

  const initialCall = useRef(null);
  initialCall.current = (source) => {
    getNotes({ objectId, requestSource: source, contentType: contentType ?? 'Stakeholder' });
  };

  useEffect(() => {
    if (notesFunctions) return; // parent (BioElements) already fetched the list
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Api call cancelled by the user');
    };
  }, []);

  const createNoteController = async (e, editor) => {
    const content = editor.getContent();
    const innerText = editor.getBody().innerText;
    if (e.keyCode === 13 && content !== '' && innerText.trim() !== '' && !e.shiftKey) {
      e.preventDefault();
      setDisabledTextArea(true);
      const addNoteProps = { objectId, text: content, contentType };
      if (newAgencyPlan) {
        addNoteProps.visibility = selectedVisibility;
      }
      await addNote(addNoteProps);
      setDisabledTextArea(false);
      editor.setContent('');
    }
  };

  const toolbarVisibilityHandler = () => {
    if (editorRef.current?.editor?.focus) {
      editorRef.current.editor.focus();
      const body = editorRef.current.editor.getBody();
      if (body) {
        const caretSpans = body.querySelectorAll('span[id="_mce_caret"]');
        caretSpans.forEach((span) => {
          span.remove();
        });
      }
    }
    if (editorRef.current?.editor?.container?.scrollHeight > 48) {
      let headerDisplayed = true;
      const editorHeader = editorRef.current?.editor?.editorContainer?.querySelector('.tox-editor-header');
      if (editorHeader) {
        headerDisplayed = window.getComputedStyle(editorHeader).display !== 'none';
      }
      const refValue = showToolbar && headerDisplayed ? -45 : 43;
      const newHeight = editorRef.current?.editor?.container?.scrollHeight + refValue;
      editorRef.current.editor.container.style.height = `${newHeight}px`;
    }
    setShowToolbar(!showToolbar);
  };

  return (
    <>
      <div className='border-bottom position-relative'>
        <div className='px-4 specific-bottom-padding-26'>
          <div
            className={`position-relative rounded border d-flex align-items-start ${
              disabledTextArea ? 'notes-textarea-disabled' : ''
            }`}
          >
            <div className='pr-1 left-16 top-9 position-absolute'>
              <img
                style={{ width: '30px', minWidth: '30px' }}
                alt='avatar'
                className='rounded-circle img-fluid'
                src={`https://cdn.auth0.com/avatars/${Array.from(user?.given_name)[0].toLowerCase()}${Array.from(
                  user?.family_name
                )[0].toLowerCase()}.png`}
              />
            </div>
            <NotesInput
              key={`${selectedVisibility}-notes-input`}
              editorRef={editorRef}
              disabled={disabledTextArea}
              onChange={createNoteController}
              index={isMobile ? null : 0}
              notes={userNotes}
              showToolbar={showToolbar}
              customClass={`main-note-input`}
            />
            <div className='d-flex notes-input-tooltip-container'>
              {newAgencyPlan && (
                <AgencyLogoAndExplanation
                  activeTab={'Notes'}
                  selectedVisibility={selectedVisibility}
                  setSelectedVisibility={setSelectedVisibility}
                  user={user}
                  contentType={contentType}
                />
              )}
              <span
                className='apply-note-formatting-button specific-x-padding-4 light-70-text light-blue-background-004-hover user-select-none d-block pointer'
                data-tooltip-content={isMobile ? '' : `${showToolbar ? 'Hide' : 'Show'} formatting`}
                data-tooltip-id='tooltip-mobile'
                data-tooltip-place='top'
                onClick={toolbarVisibilityHandler}
              >
                <span className={`d-inline-block ${showToolbar ? 'text-underline' : ''}`}>Aa</span>
              </span>
              <span className='notes-textarea-tooltip'>
                <span
                  className='icon-eye-hide'
                  data-tooltip-content='Notes are only visible to you and your team'
                  data-tooltip-id='tooltip-mobile'
                  data-tooltip-place='top'
                />
              </span>
            </div>
          </div>
        </div>
        <div className='border-top px-4 pt-3'>
          <div ref={containerRef} className='pb-3' style={{ minHeight: `${heightContainer - 1}px` }}>
            {isLoading ? (
              <NotesLoadingState />
            ) : (
              <>
                {userNotes?.length > 0 ? (
                  <>
                    {userNotes.map((item) => (
                      <UserNote
                        key={`item-${item?.id}`}
                        item={item}
                        setNoteToDeleteId={setNoteToDeleteId}
                        setShowDeletePopup={setShowDeletePopup}
                        updateNote={updateNote}
                        isNoteCreator={isNoteCreator}
                        team={team}
                        activePlan={activePlan}
                        user={user}
                        newAgencyPlan={newAgencyPlan}
                      />
                    ))}
                  </>
                ) : (
                  <div
                    className='flex-centered justify-content-center text-center'
                    style={{ height: `${heightContainer - 36}px` }}
                  >
                    <p className='main-light-text title-h5-m' style={{ maxWidth: '420px' }}>
                      {newAgencyPlan
                        ? `${
                            selectedVisibility === 'user' ? 'Notes' : 'General notes'
                          } your team adds in relation to this stakeholder ${
                            selectedVisibility === 'user' ? `for ${team?.teamName} ` : ''
                          }will appear here`
                        : `You haven’t add any notes to this stakeholder`}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <DeleteNotePopup
        noteId={noteToDeleteId}
        setShowPopup={setShowDeletePopup}
        showPopUp={showDeletePopup && !!noteToDeleteId}
        deleteNote={deleteNote}
      />
    </>
  );
};

const NotesInput = ({
  disabled,
  onChange,
  index,
  value,
  setValue = () => {},
  notes,
  showToolbar,
  editorRef,
  customClass,
  onInitFunction,
  minHeight = 48,
  customStyle = '',
  toolbarLocation = 'bottom',
  maxHeight,
  addVerticalScroll,
  colorPickerPosition,
  placeholder,
  disableAutofocus = false,
}) => {
  const contentStyle = `
    @import url('https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,400&family=Work+Sans:wght@300&display=swap');
    ol, ul { padding-inline-start: 24px; margin: 0; }
    html{
      overflow-y: hidden;
    }
    body { 
      font-family: Lato, sans-serif; 
      font-size: 16px; 
      padding-left: 12px; 
      margin-top: 12px !important;
      margin-bottom: 0 !important;
      line-height: 1.4;
      margin-left: 0px;
      color: #00122b;
    }
    p { margin: 0px; }
    a { color: #0094cc;}
    .mce-content-body [data-mce-selected="inline-boundary"] {
    background-color: transparent !important;}
    ${blockquoteStyles}
    ${customStyle}
  `;
  return (
    <div className='notes-editor influence-description w-100'>
      <DescriptionTinyEditor
        ref={editorRef}
        setValue={setValue}
        key={JSON.stringify(notes)}
        onChangeFunction={(content) => {
          setValue(content);
        }}
        index={index}
        disabled={disabled}
        placeholder={placeholder ?? 'Start typing...'}
        toolbarLocation={toolbarLocation}
        minHeight={minHeight}
        maxHeight={maxHeight}
        contentStyle={contentStyle}
        keyDownFunction={onChange}
        value={value}
        showToolbar={showToolbar}
        customClass={customClass}
        newline_behavior={'block'}
        onInitFunction={(editor) => {
          const editorContainer = editor?.editorContainer?.querySelector('.tox-editor-container');
          if (editorContainer) {
            editorContainer.style.maxHeight = 'unset';
          }
          if (onInitFunction) {
            onInitFunction(editor);
          }
        }}
        hideSkeleton={true}
        addVerticalScroll={addVerticalScroll}
        autoresize_bottom_margin={14}
        colorPickerPosition={colorPickerPosition}
        disableAutofocus={disableAutofocus}
      />
    </div>
  );
};

const UserNote = (props) => {
  const {
    item,
    setNoteToDeleteId,
    setShowDeletePopup,
    updateNote,
    isNoteCreator,
    team,
    user,
    activePlan,
    newAgencyPlan,
    customNotesInputClass = '',
    className = '',
    showTime,
  } = props;
  const { authorName, text, created, lastModified, isOrganisationWide } = item;
  const [localText, setLocalText] = useState(text);

  const [isDisabled, setIsDisabled] = useState(false);
  const [editNote, setEditNote] = useState(false);
  const isMobile = useIsMobile();

  const handleSaveNote = async (e, editor) => {
    const saveNote = async (content) => {
      const textContent = content ?? localText;
      if (text !== textContent && textContent.trim() !== '') {
        setIsDisabled(true);
        await updateNote({ note: item, text: textContent });
        setIsDisabled(false);
        setEditNote(false);
      } else {
        setEditNote(false);
      }
    };
    if (typeof e === 'undefined') {
      saveNote();
    } else if (e.keyCode === 13 && !e.shiftKey && editor) {
      const content = editor.getContent();
      const innerText = editor.getBody().innerText;
      if (e.keyCode === 13 && content !== '' && innerText.trim() !== '' && !e.shiftKey) {
        e.preventDefault();
        saveNote(content);
      }
    }
  };

  return (
    <div className={`pt-1 position-relative user-note-main-container ${className}`}>
      <div className='d-flex justify-content-between align-items-center z-max'>
        <p className='mb-0 d-flex align-items-end flex-wrap note-name-item'>
          <span className={`font-weight-bold mr-2 ${showTime === 'name' ? '' : 'mr-lg-3'} paragraph-p2`}>
            {authorName}
          </span>
          {showTime === 'name' ? (
            <span
              className={`main-light-text paragraph-p3 d-block pointer text-underline-hover paragraph-p5`}
              data-tooltip-content={time(created).formatEmbargoedDate()}
              data-tooltip-id='tooltip-mobile'
              data-tooltip-place='top'
            >
              {time(created).formatTime()}
            </span>
          ) : (
            <span className={`main-light-text paragraph-p3 d-block paragraph-p5`}>
              {time(created).formatEmbargoedDate()}
            </span>
          )}
        </p>
        <div className='row mx-0'>
          {isNoteCreator({ note: item }) && (
            <div className='user-note-action-buttons d-flex justify-content-end'>
              <button
                id='save-edit-note-btn'
                data-tooltip-content={editNote || isMobile ? '' : 'Edit note'}
                data-tooltip-id={'general-tooltip'}
                className='general-button d-inline-block mr-3'
                onClick={async () => {
                  if (editNote) {
                    await handleSaveNote();
                  } else {
                    setEditNote(true);
                  }
                }}
              >
                {editNote ? (
                  <span className='blue-text'>Save</span>
                ) : (
                  <span className='icon-edit-bold main-subtle-text paragraph-p2' />
                )}
              </button>
              <button
                data-tooltip-content={isMobile ? '' : 'Delete note'}
                data-tooltip-id={'general-tooltip'}
                className='general-button'
                onClick={(e) => {
                  setNoteToDeleteId(item?.id);
                  setShowDeletePopup(true);
                }}
              >
                <span className='icon-delete main-subtle-text paragraph-p3' />
              </button>
            </div>
          )}
          {newAgencyPlan && (
            <NoteVisibilityLogo
              isOrganisationWide={isOrganisationWide}
              team={team}
              activePlan={activePlan}
              user={user}
            />
          )}
        </div>
      </div>
      <div className={`user-note-container main-subtle-text rounded  ${isDisabled ? 'light-50-text' : ''}`}>
        {editNote ? (
          <NotesInput
            disabled={isDisabled}
            onChange={handleSaveNote}
            index={0}
            value={localText}
            setValue={setLocalText}
            customClass={`list-item-note-input ${customNotesInputClass}`}
          />
        ) : (
          <div className='d-flex align-items-start'>
            <div className='mb-0 note-container editor-content-preview flex-grow-1'>
              <div dangerouslySetInnerHTML={{ __html: text }}></div>
              {!time(created).isSame(time(lastModified)) && (
                <span className='light-50-text d-inline-block ml-1 paragraph-p3'>(edited)</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NoteVisibilityLogo = ({
  isOrganisationWide,
  team,
  activePlan,
  user,
  className = '',
  style = {},
  showTooltip,
}) => {
  return (
    <div
      className={`ml-3 ${className}`}
      style={{ marginRight: '13px', ...style }}
      data-tooltip-content={showTooltip ? (!isOrganisationWide ? team?.teamName : 'General note') : ''}
      data-tooltip-id='tooltip-mobile'
      data-tooltip-place='top'
    >
      {!isOrganisationWide ? <AgencyImage team={team} /> : <OrganizationImage activePlan={activePlan} user={user} />}
    </div>
  );
};

const ContentDeleteNotePopup = (props) => {
  const { setShowPopup, noteId, deleteNote } = props;

  return (
    <div className='p-4 px-lg-5 pt-5 pb-lg-2'>
      <div className='text-center'>
        <p>Are you sure you want to remove this note?</p>

        <div className='my-4'>
          <button
            className='general-button outline-button general-button-spacing mr-2 mr-lg-3 rounded-pill'
            onClick={() => setShowPopup()}
          >
            Cancel
          </button>
          <button
            className='general-button general-button-spacing delete-note-button text-white rounded-pill'
            onClick={async (e) => {
              let el = e.target;
              el.disabled = true;
              await deleteNote({ id: noteId });
              setShowPopup();
            }}
          >
            {`Delete note`}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteNotePopup = (props) => {
  const { showPopUp, setShowPopup } = props;
  return (
    <Portal>
      <ModalComponent maxWidth={480} isOpen={showPopUp} setIsOpen={setShowPopup}>
        <ContentDeleteNotePopup {...props} />
      </ModalComponent>
    </Portal>
  );
};

const NotesLoadingState = () => {
  return (
    <>
      {Array(2)
        .fill()
        .map((item, index) => (
          <SkeletonTheme baseColor='#fff' key={index}>
            <div className='py-3 position-relative'>
              <div className='flex-centered margin-small'>
                <div className='pr-2'>
                  <Skeleton height={30} width={30} circle={true} />
                </div>
                <Skeleton height={19} width={`200px`} />
              </div>
              <div className='border py-2 px-3 rounded'>
                <Skeleton height={19} width={`100%`} />
              </div>
            </div>
          </SkeletonTheme>
        ))}
    </>
  );
};

export { NotesLoadingState, UserNote, NoteVisibilityLogo, DeleteNotePopup, ContentDeleteNotePopup, NotesInput };
export default Notes;
